import { api } from '@api'
import { useQuery } from '@tanstack/solid-query'
import { createMemo } from 'solid-js'

const fetchCourses = async () => {
	const { data, error } = await api().rpc('get_courses_with_progress')
	if (error) throw error
	return data
}

export function useCourses() {
	const coursesQuery = useQuery(() => ({
		queryKey: ['courses'],
		queryFn: () => fetchCourses(),
		staleTime: 1000 * 60 * 5,
	}))

	// 1. The "Active" Course
	// Assumes your RPC is ordered by activity (as discussed previously)
	const activeCourse = createMemo(() => coursesQuery.data?.[0] || null)

	// 2. Total Curriculum Completion Percentage
	// Calculates (Sum of all completed / Sum of all total)
	const totalCompletionPercent = createMemo(() => {
		const courses = coursesQuery.data || []
		if (courses.length === 0) return 0

		const total = courses.reduce((acc, c) => acc + Number(c.total_lessons || 0), 0)
		const completed = courses.reduce(
			(acc, c) => acc + Number(c.completed_lessons || 0),
			0,
		)

		return total > 0 ? Math.round((completed / total) * 100) : 0
	})

	// 3. Current Course Percent Complete
	const activeCoursePercent = createMemo(() => {
		const course = activeCourse()
		if (!course || !course.total_lessons) return 0
		return Math.round(
			(Number(course.completed_lessons) / Number(course.total_lessons)) * 100,
		)
	})

	// 4. Current Course Title
	const activeCourseTitle = createMemo(
		() => activeCourse()?.title || 'No active course',
	)

	// 5. Link to Active Course
	const activeCourseLink = createMemo(() => {
		const course = activeCourse()
		return course ? `/courses/${course.slug}` : '#'
	})

  const showActiveCourse = createMemo(() => {
    const course = activeCourse()
    if (!course) return false

    const percent = activeCoursePercent()
    return percent < 100
  })

	return {
		coursesQuery,
		totalCompletionPercent,
		activeCoursePercent,
		activeCourseTitle,
		activeCourseLink,
		activeCourse,
    showActiveCourse
	}
}
